// client/src/stores/useAuthStore.ts
//
// [fix-export]       aggiunto named export — i test usano { useAuthStore }
// [fix-snapshot]     updateAccess allineato con AuthStoreSnapshot (user?: User)
// [fix-duplication]  logout e clearAuth condividono _doLogout() privata
// [fix-persist]      accessToken salvato in localStorage per sopravvivere
//                    alla distruzione WebView su Android (cambio app / screen off)
// [fix-abort]        AbortError (timeout) NON deve fare logout — il server
//                    Render free tier può impiegare 30-60s in cold start
// FASE 4:           Token salvato ANCHE in @capacitor/preferences (storage nativo)
//                    per sopravvivere alla distruzione COMPLETA della WebView.
//                    Preferences usa SharedPreferences (Android) / NSUserDefaults (iOS).

import { create } from "zustand";
import { AuthState, User, PremiumState } from "@/types";
import { resolveApiBaseUrl } from "@/utils/apiBase";
import { resetNativePush } from "@/services/nativePush";
import { hasActiveRecordingSession } from "@/services/gpsTrackPersistence";
import useUiStore from "@/store/useUiStore";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
// FASE 1.5: Keepalive per prevenire cold start Render
import { startKeepalive, stopKeepalive } from "@/services/keepalive";
// FIX REFRESH-RACE-LOGOUT (20 Set 2026): refresh CONDIVISO (singleton) —
// tokenRefresh.ts NON importa lo store (azioni iniettate) → nessun ciclo.
import { sharedRefresh } from "@/api/auth/tokenRefresh";
import { SessionExpiredError } from "@/api/core/errors";

const LS_USER = "rideconnect_user";
const LS_TOKEN = "rideconnect_token";

// FASE 4: Capacitor Preferences keys per storage nativo
// Sopravvive alla distruzione completa della WebView (Android/iOS)
const PREF_USER = "rideconnect_user";
const PREF_TOKEN = "rideconnect_token";

const API_BASE: string = resolveApiBaseUrl();

// Timeout aumentato a 25s per accomodare cold start Render free tier (30-60s)
// ma senza bloccare l'UI troppo a lungo. Il keepalive periodico ritenterà.
const REFRESH_TIMEOUT_MS = 25_000;

// FIX REFRESH-401-NOISE (16 Set 2026): quando /refresh risponde 401/403 ma
// l'access token è ancora valido, il cookie di refresh non è disponibile
// (cookie di terze parti bloccato, sessione ruotata su altro device o cap
// sessioni raggiunto). In quel caso ritentare ogni 10 minuti produce solo
// rumore rosso in console (POST /api/auth/refresh 401) SENZA alcun beneficio:
// il cookie non torna da solo. Backoff: niente refresh per 30 minuti.
const REFRESH_BACKOFF_MS = 30 * 60 * 1000;
let refreshBackoffUntil = 0;

export function clearRefreshBackoff(): void {
  refreshBackoffUntil = 0;
}

// ─── FUS FIX (Set 2026): decodifica JWT + stato premium sincrono ─────────────
//
// Il server ora mette is_premium / premium_tier / premium_until nei claim
// del JWT (login, google, refresh). Qui li decodifichiamo in modo SINCRONO
// al momento dell'idratazione: l'utente Pro è riconosciuto IMMEDIATAMENTE
// al primo render (niente banner "Fai l'upgrade" per alcuni secondi).
// Il re-check server resta in background (stale-while-revalidate) tramite
// GET /api/subscription/status nel hook useSubscriptionStatus.

function decodeJwtPayload(token: string | null): Record<string, any> | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, any>;
  } catch {
    return null;
  }
}

const NO_PREMIUM: PremiumState = { isPremium: false, tier: null, until: null };

/** Stato premium sincrono dai claim JWT (fallback: campi user object). */
export function decodePremiumFromToken(
  token: string | null,
  user?: User | null,
): PremiumState {
  const payload = decodeJwtPayload(token);
  const isPremium = payload?.is_premium === true;
  const tier = (payload?.premium_tier as string | null) ?? null;
  const untilRaw = payload?.premium_until;
  const until =
    typeof untilRaw === "number" && Number.isFinite(untilRaw) ? untilRaw : null;

  // FALLBACK: token legacy senza claim → campi is_premium/premium_until
  // dello user object (già forniti dal server in login/refresh).
  if (payload && typeof payload.is_premium !== "boolean") {
    if (user?.is_premium === true) {
      const uUntil =
        typeof user.premium_until === "number" ? user.premium_until : null;
      // Scaduto → NON premium (fallback propone l'upgrade solo a scadenza
      // effettiva, FUS FIX requisito #4).
      if (uUntil != null && uUntil <= Date.now()) {
        return {
          isPremium: false,
          tier: user.subscription_tier ?? null,
          until: uUntil,
        };
      }
      return {
        isPremium: true,
        tier: user.subscription_tier ?? null,
        until: uUntil,
      };
    }
    return NO_PREMIUM;
  }

  if (!isPremium) return NO_PREMIUM;

  // Premium con scadenza nel passato → non più premium (login successivo
  // alla scadenza: si propone l'upgrade solo ora, non prima).
  if (until != null && until <= Date.now()) {
    return { isPremium: false, tier, until };
  }
  return { isPremium: true, tier, until };
}

/** Verifica che un access token JWT sia ancora valido (exp nel futuro). */
function isAccessTokenValid(token: string | null): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  return (
    typeof payload.exp === "number" && payload.exp * 1000 > Date.now() + 30_000
  );
}

// FASE 4: Helper per salvare token su Capacitor Preferences (storage nativo)
async function _saveToNativePrefs(
  user: User | null,
  token: string | null,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (user && token) {
      await Promise.all([
        Preferences.set({ key: PREF_USER, value: JSON.stringify(user) }),
        Preferences.set({ key: PREF_TOKEN, value: token }),
      ]);
    } else {
      await Promise.all([
        Preferences.remove({ key: PREF_USER }),
        Preferences.remove({ key: PREF_TOKEN }),
      ]);
    }
  } catch {
    // Preferences non disponibile (es. Web pure) — ignora silenziosamente
  }
}

// FIX (Luglio 2026): safeJsonParse — evita crash su valori corrotti in localStorage/Preferences
function safeJsonParse<T>(raw: string | null | undefined): T | null {
  if (!raw || raw === "undefined" || raw === "null") return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Millisecondi rimanenti prima della scadenza dell'access token JWT.
 * Negative se il token è scaduto o non decodificabile.
 */
function getAccessTokenRemainingMs(token: string | null): number {
  if (!token) return -1;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return -1;
  return payload.exp * 1000 - Date.now();
}

// FIX REFRESH NOISE (Ago 2026): non chiamare /api/auth/refresh quando il
// token è ancora valido per più di questa soglia. Il cookie di refresh può
// essere scaduto/ruotato (es. multi-device) → 401 rumorosi in console ogni
// 10 minuti. Il refresh si fa solo in prossimità della scadenza.
const REFRESH_MARGIN_MS = 10 * 60 * 1000; // 10 minuti

// FASE 4: Helper per recuperare token da Capacitor Preferences
async function _loadFromNativePrefs(): Promise<{
  user: User | null;
  token: string | null;
}> {
  if (!Capacitor.isNativePlatform()) return { user: null, token: null };
  try {
    const [userResult, tokenResult] = await Promise.all([
      Preferences.get({ key: PREF_USER }),
      Preferences.get({ key: PREF_TOKEN }),
    ]);
    return {
      user: safeJsonParse<User>(userResult.value),
      token: tokenResult.value || null,
    };
  } catch {
    return { user: null, token: null };
  }
}

// Pulizia legacy tokens
try {
  localStorage.removeItem("rideconnect_refresh_token");
} catch {
  /* ignore */
}
try {
  localStorage.removeItem("rideconnect_access_token");
} catch {
  /* ignore */
}

let hydrationPromise: Promise<void> | null = null;

const _storedUser: User | null = safeJsonParse<User>(
  localStorage.getItem(LS_USER),
);
const _storedToken: string | null = localStorage.getItem(LS_TOKEN);

// FUS FIX (Set 2026): premium decodificato SINCRONAMENTE al primo import —
// disponibile fin dal primo render, anche prima di initSession().
const _storedPremium: PremiumState = decodePremiumFromToken(
  _storedToken,
  _storedUser,
);

// ── Helper privato condiviso da logout + clearAuth ────────────────────────────
// FIX LOGOUT-STORM (21 Ott 2026): all'avvio con sessione scaduta partivano 10
// richieste parallele, ognuna riceveva 401 e chiamava logout → 19 POST
// /api/auth/logout in 7 secondi (misurato sul campo). Ora:
//  - se non c'è sessione da revocare, la POST non parte affatto;
//  - una sola POST alla volta (le chiamate concorrenti vengono ignorate).
let _logoutPostInFlight = false;
let _lastLogoutPostAt = 0;
/** Finestra in cui ulteriori logout non ripetono la POST (revoca già inviata). */
const LOGOUT_POST_COOLDOWN_MS = 30_000;

function _doLogout(): void {
  const state = useAuthStore.getState();
  const now = Date.now();
  const hasSessionToRevoke =
    _logoutPostInFlight === false &&
    now - _lastLogoutPostAt > LOGOUT_POST_COOLDOWN_MS &&
    (state.isAuthenticated || !!state.accessToken || !!_storedToken);
  if (hasSessionToRevoke) {
    _logoutPostInFlight = true;
    _lastLogoutPostAt = now;
    fetch(`${API_BASE}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    })
      .catch(() => {
        /* offline — ignora */
      })
      .finally(() => {
        _logoutPostInFlight = false;
      });
  }
  // GOOGLE PLAY QUALITY 2027 (Fase 3 — Zero-Tap Sign-In): la restore key va
  // REVOCATA al logout. Credential Manager è stateless: senza questa chiamata
  // la chiave sopravvivrebbe al sign-out e il prossimo avvio ripristinerebbe
  // la sessione dell'utente precedente. Best-effort, mai bloccante.
  void import("@/services/restoreCredential")
    .then((m) => m.clearRestoreKey())
    .catch(() => {
      /* best-effort */
    });
  localStorage.removeItem(LS_USER);
  localStorage.removeItem(LS_TOKEN);
  // FASE 4: Pulisci anche Capacitor Preferences (storage nativo)
  _saveToNativePrefs(null, null).catch(() => {});
  // FASE 1.5: Ferma keepalive (non serve più tenere caldo il server)
  stopKeepalive();
}

// ── FEAT NO-LOGOUT-DURING-RECORDING (18 Set 2026) ────────────────────────────
// Se l'utente sta registrando un percorso (lock UI attivo o sessione
// IndexedDB con waypoint) un logout da scadenza sessione farebbe perdere
// la registrazione in corso. Controlliamo entrambe le fonti.
async function isRecordingInProgress(): Promise<boolean> {
  try {
    if (useUiStore.getState().recordingLock) return true;
    return await hasActiveRecordingSession();
  } catch {
    return false;
  }
}

// ── Store ─────────────────────────────────────────────────────────────────────

// [fix-export] named export — import { useAuthStore } funziona nei test
// il default export resta per compatibilità con i callsite esistenti
//
// [fix-persist] accessToken viene ora salvato in localStorage insieme allo user.
// Questo permette alla sessione di sopravvivere alla distruzione della WebView
// Android (cambio app, spegnimento schermo, liberazione memoria OS).
export const useAuthStore = create<AuthState>((set, get) => ({
  user: _storedUser,
  accessToken: _storedToken,
  refreshToken: null,
  isHydrated: false,
  isAuthenticated: _storedUser !== null,
  impersonationAdminToken: null,
  impersonatedUser: null,
  premium: _storedPremium,

  /**
   * LOCALE/TAPPA (Ott 2026): true mentre stiamo verificando se l'account è un
   * locale (una sola chiamata a /api/auth/me all'avvio). Le aree protette
   * aspettano questo flag invece di bloccare per sempre l'app.
   */
  localePending: false,

  /** Unisce i campi aggiornati del profilo (es. is_locale) e li persiste. */
  applyUserPatch: (patch: Partial<User>) => {
    const current = get().user;
    if (!current) return;
    const next = { ...current, ...patch } as User;
    try {
      localStorage.setItem(LS_USER, JSON.stringify(next));
      const token = get().accessToken;
      if (token) _saveToNativePrefs(next, token).catch(() => {});
    } catch {
      /* storage non disponibile: lo stato in memoria resta valido */
    }
    set({ user: next });
  },

  // FIX-C1 + FIX-AUTH-1 + FIX-AUTH-IS-1 + FIX-PERSIST + FASE 4 (native prefs)
  // V-03: Dopo l'approvazione dealer da parte dell'admin, il JWT esistente
  // ha ancora user_type='private'. Il dealer deve effettuare logout/login
  // per ottenere un nuovo token con user_type='dealer'.
  // La push notification 'dealer_approved' include requireRelogin:true.
  setSession: (user: User, accessToken: string) => {
    clearRefreshBackoff(); // login/refresh riuscito → riattiva il refresh
    localStorage.setItem(LS_USER, JSON.stringify(user));
    localStorage.setItem(LS_TOKEN, accessToken);
    // FASE 4: Salva anche su Capacitor Preferences (sopravvive a WebView kill)
    _saveToNativePrefs(user, accessToken).catch(() => {});
    // FASE 1.5: Avvia keepalive per prevenire cold start Render
    startKeepalive();
    hydrationPromise = null;
    set({
      user,
      accessToken,
      refreshToken: null,
      isAuthenticated: true,
      premium: decodePremiumFromToken(accessToken, user),
    });
  },

  // [fix-snapshot] user è opzionale — allineato con AuthStoreSnapshot
  // in apiFetch.ts che dichiara updateAccess(accessToken, user?: unknown)
  // [fix-persist] token salvato in localStorage per sopravvivenza WebView
  // FASE 4: Salvato anche su Capacitor Preferences
  updateAccess: (accessToken: string, user?: User) => {
    const isImpersonating = !!get().impersonationAdminToken;
    if (isImpersonating) {
      set((prev) => ({
        ...prev,
        accessToken,
        refreshToken: null,
        premium: decodePremiumFromToken(accessToken, prev.user),
      }));
      return;
    }
    localStorage.setItem(LS_TOKEN, accessToken);
    if (user) localStorage.setItem(LS_USER, JSON.stringify(user));
    const nextUser = user ?? get().user;
    // FASE 4: Salva anche su Capacitor Preferences (storage nativo)
    _saveToNativePrefs(nextUser, accessToken).catch(() => {});
    set((prev) => ({
      ...prev,
      accessToken,
      refreshToken: null,
      user: nextUser,
      isAuthenticated: nextUser !== null,
      premium: decodePremiumFromToken(accessToken, nextUser),
    }));
  },

  logout: () => {
    _doLogout();
    // Resetta registrazione FCM per permettere nuovo token al prossimo login
    try {
      resetNativePush();
    } catch {
      /* non-critical */
    }
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      impersonationAdminToken: null,
      impersonatedUser: null,
      premium: NO_PREMIUM,
      localePending: false,
    });
  },

  clearAuth: () => {
    _doLogout();
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      impersonationAdminToken: null,
      impersonatedUser: null,
      premium: NO_PREMIUM,
      localePending: false,
    });
  },

  // FEAT NO-LOGOUT-DURING-RECORDING: scadenza sessione con registrazione
  // attiva → niente logout, backoff breve e refresh riprova tra poco.
  expireSession: async (): Promise<void> => {
    if (await isRecordingInProgress()) {
      refreshBackoffUntil = Date.now() + 60_000;
      console.warn(
        "[Auth] Sessione scaduta ma registrazione attiva — logout rimandato (riprovo il refresh tra 1 min).",
      );
      return;
    }
    get().logout();
  },

  startImpersonation: (impersonatedUser: User, impersonatedToken: string) => {
    const adminToken = get().accessToken;
    hydrationPromise = Promise.resolve();
    set({
      impersonationAdminToken: adminToken,
      impersonatedUser,
      user: impersonatedUser,
      accessToken: impersonatedToken,
      isAuthenticated: true,
      premium: decodePremiumFromToken(impersonatedToken, impersonatedUser),
    });
  },

  stopImpersonation: () => {
    const adminToken = get().impersonationAdminToken;
    const adminUser: User | null = safeJsonParse<User>(
      localStorage.getItem(LS_USER),
    );

    // FIX BUG #5: Se localStorage è stato pulito (browser cache clear, policy OS),
    // adminUser può essere null mentre adminToken è ancora in memoria.
    // In quel caso forziamo un logout completo per evitare stato inconsistente.
    if (!adminUser || !adminToken) {
      console.warn(
        "[Auth] Impersonation recovery failed — forcing full logout",
      );
      _doLogout();
      try {
        const { resetNativePush } = require("@/lib/nativePush");
        resetNativePush();
      } catch {
        /* non-critical */
      }
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        impersonationAdminToken: null,
        impersonatedUser: null,
        premium: NO_PREMIUM,
      });
      return;
    }

    hydrationPromise = null;
    set({
      user: adminUser,
      accessToken: adminToken,
      isAuthenticated: true,
      impersonationAdminToken: null,
      impersonatedUser: null,
      premium: decodePremiumFromToken(adminToken, adminUser),
    });
  },

  getEffectiveToken: (): string | null => {
    return get().accessToken;
  },

  getEffectiveUser: (): User | null => {
    const state = get();
    return state.impersonationAdminToken !== null
      ? state.impersonatedUser
      : state.user;
  },

  // ── forceRefreshToken ─────────────────────────────────────────────────────
  // Chiamata diretta a /api/auth/refresh SENZA il guard hydrationPromise.
  // Usata da useSessionKeepalive per refresh periodici del token senza
  // l'hack di reset della promise. Gestisce sia successo che fallimento
  // (401/403 → logout automatico).
  // [fix-persist] Salva il nuovo token anche in localStorage.
  forceRefreshToken: async (force: boolean = false): Promise<void> => {
    const storedUser = localStorage.getItem(LS_USER);
    if (!storedUser) return;

    // FIX REFRESH-401-NOISE: backoff dopo un 401 recente (cookie refresh
    // indisponibile ma access token valido) — evita 401 ripetuti ogni 10'.
    if (refreshBackoffUntil > Date.now()) return;

    // FIX REFRESH NOISE: se il token è ancora valido oltre il margine,
    // non serve chiamare /refresh — evita 401 rumorosi quando il cookie
    // di refresh è stantio (multi-device) ma l'access token funziona.
    // force=true (initSession) mantiene il comportamento originale per
    // riallineare user/tier dal server all'avvio.
    if (!force) {
      const tok = get().accessToken ?? localStorage.getItem(LS_TOKEN);
      if (getAccessTokenRemainingMs(tok) > REFRESH_MARGIN_MS) return;
    }

    try {
      // FIX REFRESH-RACE-LOGOUT (20 Set 2026): refresh CONDIVISO (singleton
      // in tokenRefresh.ts) — niente più corse tra apiFetch e store.
      const data = await sharedRefresh();
      const freshUser = data.user as User;
      refreshBackoffUntil = 0; // refresh riuscito → reset backoff
      localStorage.setItem(LS_USER, JSON.stringify(freshUser));
      localStorage.setItem(LS_TOKEN, data.accessToken);
      set({
        user: freshUser,
        accessToken: data.accessToken,
        refreshToken: null,
        isAuthenticated: true,
        premium: decodePremiumFromToken(data.accessToken, freshUser),
      });
    } catch (err: unknown) {
      // Errore di rete: non facciamo logout (disconnessione temporanea).
      if (!(err instanceof SessionExpiredError)) return;

      // Sessione scaduta secondo il server: controlla l'access token —
      // se è ANCORA VALIDO (cookie ruotato/race) niente logout, backoff.
      const tok = get().accessToken ?? localStorage.getItem(LS_TOKEN);
      if (isAccessTokenValid(tok)) {
        refreshBackoffUntil = Date.now() + REFRESH_BACKOFF_MS;
        return;
      }
      // FEAT NO-LOGOUT-DURING-RECORDING: registrazione in corso →
      // niente logout; backoff breve e riprova al prossimo giro.
      if (await isRecordingInProgress()) {
        refreshBackoffUntil = Date.now() + 60_000;
        return;
      }
      // FIX REFRESH-RACE-LOGOUT: ULTIMO tentativo tollerante — un solo
      // retry dopo 2s prima di dichiarare la sessione morta (il cookie
      // potrebbe essere stato appena ruotato da un refresh parallelo).
      await new Promise((r) => setTimeout(r, 2_000));
      try {
        const data = await sharedRefresh();
        const freshUser = data.user as User;
        refreshBackoffUntil = 0;
        localStorage.setItem(LS_USER, JSON.stringify(freshUser));
        localStorage.setItem(LS_TOKEN, data.accessToken);
        set({
          user: freshUser,
          accessToken: data.accessToken,
          refreshToken: null,
          isAuthenticated: true,
          premium: decodePremiumFromToken(data.accessToken, freshUser),
        });
        return;
      } catch {
        /* secondo fallimento: logout reale sotto */
      }
      // FIX LOGOUT-CONSISTENTE (20 Set 2026): pulisci ANCHE le Capacitor
      // Preferences — altrimenti al prossimo avvio la sessione "loggata
      // fuori" veniva ripristinata dallo storage nativo (stato incoerente).
      localStorage.removeItem(LS_USER);
      localStorage.removeItem(LS_TOKEN);
      _saveToNativePrefs(null, null).catch(() => {});
      stopKeepalive();
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        premium: NO_PREMIUM,
      });
    }
  },

  initSession: (): Promise<void> => {
    if (hydrationPromise) return hydrationPromise;

    hydrationPromise = (async () => {
      // FIX #BUG-STALE-TIER (Ago 2026): prima, se accessToken era già in
      // memoria (app riaperta con localStorage pieno), il refresh veniva
      // SALTATO ("sessione valida") e lo user salvato restava STALE:
      // es. l'admin assegnava il piano rider_plus ma l'app continuava a
      // mostrare "Free" finché l'utente non faceva logout+login manuale.
      // Ora, anche con token già presente, parte SEMPRE un refresh
      // silenzioso in background (forceRefreshToken) che riallinea user
      // e token dal server senza bloccare lo startup.
      if (get().accessToken) {
        set({ isHydrated: true });
        get()
          .forceRefreshToken(true)
          .catch(() => {
            // Fallimento silenzioso — il token salvato potrebbe ancora funzionare
          });
        return;
      }

      // FASE 4: Se localStorage è vuoto (WebView distrutta su Android),
      // prova a recuperare da Capacitor Preferences (storage nativo).
      let storedUser = localStorage.getItem(LS_USER);
      let storedToken = localStorage.getItem(LS_TOKEN);

      if (!storedUser || !storedToken) {
        const native = await _loadFromNativePrefs();
        if (native.user && native.token) {
          // Recupero da storage nativo riuscito — ripristina in localStorage
          storedUser = JSON.stringify(native.user);
          storedToken = native.token;
          localStorage.setItem(LS_USER, storedUser);
          localStorage.setItem(LS_TOKEN, storedToken);
          console.log(
            "[Auth] Session recovered from native Capacitor Preferences",
          );
        }
      }

      if (!storedUser) {
        set({ isHydrated: true, isAuthenticated: false });
        return;
      }

      // [fix-abort] NON facciamo logout se abbiamo già user+token salvati.
      // L'utente ha fatto login in precedenza. Se il server è in cold start
      // (Render free tier = 30-60s), il refresh può fallire per timeout.
      // In quel caso usiamo il token salvato e ritentiamo dopo.
      // Solo se NON abbiamo proprio il token salvato proviamo il refresh.
      // FASE 4: storedToken già recuperato sopra (da localStorage o native prefs)
      if (storedToken) {
        // Abbiamo token salvato: usiamolo subito, refresh in background
        const user = safeJsonParse<User>(storedUser);
        set({
          user,
          accessToken: storedToken,
          refreshToken: null,
          isHydrated: true,
          isAuthenticated: true,
          premium: decodePremiumFromToken(storedToken, user),
        });
        // Refresh silenzioso in background per rinnovare il token
        get()
          .forceRefreshToken(true)
          .catch(() => {
            // Fallimento silenzioso — il token salvato potrebbe ancora funzionare
          });
        return;
      }

      // Nessun token salvato: dobbiamo fare refresh per forza
      set({ isHydrated: true });

      try {
        // FIX REFRESH-RACE-LOGOUT (20 Set 2026): refresh CONDIVISO (singleton)
        const data = await sharedRefresh();
        if (data.accessToken && data.user) {
          const freshUser = data.user as User;
          localStorage.setItem(LS_USER, JSON.stringify(freshUser));
          localStorage.setItem(LS_TOKEN, data.accessToken);
          set({
            user: freshUser,
            accessToken: data.accessToken,
            refreshToken: null,
            isAuthenticated: true,
            premium: decodePremiumFromToken(data.accessToken, freshUser),
          });
        }
      } catch (err: unknown) {
        if (err instanceof SessionExpiredError) {
          // FEAT NO-LOGOUT-DURING-RECORDING: registrazione attiva →
          // rimanda il logout (backoff 60s) per non perdere il percorso.
          if (await isRecordingInProgress()) {
            refreshBackoffUntil = Date.now() + 60_000;
            return;
          }
          // FIX REFRESH-RACE-LOGOUT: ULTIMO tentativo tollerante — il 401
          // potrebbe essere la coda di una rotazione parallela del refresh
          // token (il cookie nuovo è già nel WebView) → retry dopo 2s.
          await new Promise((r) => setTimeout(r, 2_000));
          try {
            const retryData = await sharedRefresh();
            if (retryData.accessToken && retryData.user) {
              const freshUser = retryData.user as User;
              localStorage.setItem(LS_USER, JSON.stringify(freshUser));
              localStorage.setItem(LS_TOKEN, retryData.accessToken);
              set({
                user: freshUser,
                accessToken: retryData.accessToken,
                refreshToken: null,
                isAuthenticated: true,
                premium: decodePremiumFromToken(
                  retryData.accessToken,
                  freshUser,
                ),
              });
              return;
            }
          } catch {
            /* secondo fallimento: logout reale sotto */
          }
          localStorage.removeItem(LS_USER);
          localStorage.removeItem(LS_TOKEN);
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            premium: NO_PREMIUM,
          });
          return;
        }

        // [fix-abort] QUALSIASI errore di rete (incluso AbortError/timeout)
        // NON deve fare logout. Il server potrebbe essere in cold start.
        // Manteniamo i dati utente in localStorage e l'utente resta loggato.
        // Il keepalive periodico o il prossimo resume ritenterà il refresh.
        //
        // Se NON abbiamo token salvato e NON siamo riusciti a fare refresh,
        // lasciamo isAuthenticated = true (lo user è in localStorage)
        // ma accessToken = null. Layout.tsx gestirà questo caso con
        // un retry invece di redirect immediato a /login.
        if (!get().accessToken && !storedToken) {
          // Caso estremo: nessun token in memoria né in localStorage
          // e refresh fallito. Manteniamo lo user ma forziamo re-login.
          localStorage.removeItem(LS_USER);
          localStorage.removeItem(LS_TOKEN);
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            premium: NO_PREMIUM,
          });
        }
      }
    })();

    return hydrationPromise;
  },
}));

export default useAuthStore;
